// Helmet: Dynamic page title
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import {
  GuardedRoute,
} from './components';
import { IS_AIRTABLE_ENABLED } from './constant/integrations';
import './css/App.css';
import './css/bootstrap.min.css';
import './css/themify-icons.css';
import { ApiManager, SiteManager } from './managers';
import {
  AirtableMap,
  Emails,
  Home,
  JobManager,
  Logout,
  ModelManager,
  NoMatch,
  Reports,
} from './pages';
import { Login } from './pages/Login';
import { AppContainer } from './App.styles';
import { schemaFieldToFormInput, Alert, AlertProvider, Footer, NavigationBar, NavigationBarProvider, NavList, PageController } from '@bit-by-bit-software/ui';
import ApiRequest from './api/request';
import { CookieValues, Model, ModelNameEnum, SendRequest } from '@bit-by-bit-software/types';

function App() {
  const [cookies, setCookies] = useCookies<'token', CookieValues>(['token']);

  const [windowSize, setWindowSize] = useState<number>(window.innerWidth);
  const [pageComponents, setPageComponents] = useState<Map<any, any>>();

  const sendGetDataRequest: SendRequest<{params: { display?: boolean | undefined}, model: ModelNameEnum; }, { model: Model; results: any[] }> = ApiRequest.sendRequest;

  useEffect(() => {
    ApiManager.Init(cookies.token);

    window.addEventListener('resize', () => {
      setWindowSize(window.innerWidth);
    });

    const initSiteManager = async () => {
      const siteManager = SiteManager.GetInstance();
      await siteManager.init(cookies.token);

      // Build page/form input map
      const pageComponents = new Map();
      const pageNames = siteManager.getPageNames();
      for (let i = 0; i < pageNames.length; i++) {
        const formInputs = [];
        const page = siteManager.getPageData(pageNames[i]);

        // Iterate components in page and pre-process them
        for (let i = 0; i < page.components.length; i++) {
          // Handle form
          if (page.components[i].type === 'form') {
            // Now iterate schema fields and convert to form inputs
            for (let j = 0; j < page.components[i].form.length; j++) {
              const formInput = await schemaFieldToFormInput(
                page.components[i].form[j],
                sendGetDataRequest,
                cookies.token,
              );
              formInputs.push(formInput);
            }

            page.components[i].form = formInputs;
          }
        }

        pageComponents.set(page.name, page.components);

        setPageComponents(pageComponents);
      }
    };

    initSiteManager();
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
  },   /* eslint-disable-next-line react-hooks/exhaustive-deps */ [cookies.token]);

  const isMobile = windowSize <= 768;
  const isLoggedIn = !!cookies.token;

  const apiManager = ApiManager.GetInstance();

  if (!apiManager) {
    return null;
  }

  return (
    <AppContainer>
      <Helmet>
        <title>Checkin</title>
      </Helmet>
      <AlertProvider>
        <Router>
          <NavigationBarProvider sendRequest={ApiRequest.sendRequest} token={cookies.token} user={cookies.user}>
            <div className="app-wrapper">
              <NavigationBar isMobile={isMobile} />
              <Alert />
              <div className="pane-holder">
                {!isMobile && isLoggedIn && (
                  <div className="left-pane">
                    <NavList />
                  </div>
                )}
                <div className={`center-pane center-pane-${isLoggedIn ? '80' : '100'}`}>
                  <Routes>
                    <Route path="/" element={<Login />} />
                    {IS_AIRTABLE_ENABLED ? (
                      <Route
                        path="/airtable"
                        element={
                          <GuardedRoute authorizationTypes={['admin', 'customer']}>
                            <AirtableMap />
                          </GuardedRoute>
                        }
                      />
                    ) : null}
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/model-manager"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <ModelManager />
                        </GuardedRoute>
                      }
                    />
                    <Route
                      path="/jobs"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <JobManager />
                        </GuardedRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <Reports />
                        </GuardedRoute>
                      }
                    />
                    <Route
                      path="/emails"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <Emails />
                        </GuardedRoute>
                      }
                    />
                    {false && (
                      <Route
                        path="/register"
                        element={
                          <PageController
                            pageComponents={[...(pageComponents?.get('register') ?? [])]} 
                            sendRequest={ApiRequest.sendRequest} 
                            setCookies={setCookies} 
                            token={cookies.token}                       
                          />
                        }
                      />
                    )}
                    <Route
                      path="/home"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <Home 
                            sendRequest={ApiRequest.sendRequest} 
                            token={cookies.token} 
                          />
                        </GuardedRoute>
                      }
                    />
                    <Route path="/logout" element={<Logout />} />
                    <Route
                      path="/reset"
                      element={
                        <PageController
                          pageComponents={[...(pageComponents?.get('reset') ?? [])]}
                          sendRequest={ApiRequest.sendRequest} 
                          setCookies={setCookies} 
                          token={cookies.token}       
                        />
                      }
                    />
                    <Route
                      path="/confirm-reset"
                      element={
                        <PageController
                          pageComponents={[...(pageComponents?.get('confirm-reset') ?? [])]}
                          sendRequest={ApiRequest.sendRequest} 
                          setCookies={setCookies} 
                          token={cookies.token}       
                        />
                      }
                    />
                    <Route element={<NoMatch />} />
                  </Routes>
                </div>
              </div>
            </div>
          </NavigationBarProvider>
        </Router>
      </AlertProvider>
      <Footer />
    </AppContainer>
  );
}

export default App;
